import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { User, Mail, Shield, MapPin, Camera, X, Check, AlertCircle } from 'lucide-react';
import { UserProfile } from '../../types';

export const Profile: React.FC = () => {
  const { user } = useOutletContext<{ user: UserProfile }>();
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  
  const [profile, setProfile] = useState({
      name: user.name,
      role: user.role === 'SuperAdmin' ? 'Super Administrator' : 'Administrator',
      email: user.email,
      location: user.location || 'Lagos, Nigeria',
      profilePhoto: ''
  });

  const [editForm, setEditForm] = useState(profile);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Handle Input Changes in Edit Mode
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
              setEditForm(prev => ({ ...prev, profilePhoto: reader.result as string }));
          };
          reader.readAsDataURL(file);
      }
  };

  // Save Profile Changes
  const handleSaveProfile = () => {
      setProfile(editForm);
      setIsEditing(false);
      alert("Profile updated successfully!");
  };

  // Cancel Editing
  const handleCancelEdit = () => {
      setEditForm(profile);
      setIsEditing(false);
  };

  // Change Password Logic
  const handleChangePassword = (e: React.FormEvent) => {
      e.preventDefault();
      setIsPasswordModalOpen(false);
      alert("Password updated successfully!");
  };

  // Toggle 2FA
  const toggleTwoFactor = () => {
      const newState = !twoFactorEnabled;
      setTwoFactorEnabled(newState);
      if (newState) {
          alert("Two-Factor Authentication Enabled via Email.");
      } else {
          alert("Two-Factor Authentication Disabled.");
      }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h2>

      {/* Main Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-indigo-600 to-blue-500"></div>
        
        <div className="px-8 pb-8">
            {/* Avatar & Action Button */}
            <div className="relative flex justify-between items-end -mt-12 mb-6">
                <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-white dark:bg-gray-800 p-1 shadow-sm overflow-hidden">
                        {profile.profilePhoto ? (
                            <img src={profile.profilePhoto} alt={profile.name} className="w-full h-full object-cover rounded-full" />
                        ) : (
                            <div className="w-full h-full rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-primary dark:text-indigo-200 text-2xl font-bold border-2 border-white dark:border-gray-700">
                                {profile.name.split(' ').map(n => n[0]).join('')}
                            </div>
                        )}
                    </div>
                    <button onClick={() => avatarInputRef.current?.click()} className="absolute bottom-0 right-0 p-1.5 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">
                        <Camera className="w-4 h-4" />
                    </button>
                    <input type="file" ref={avatarInputRef} onChange={handleAvatarChange} accept="image/*" className="hidden" />
                </div>
                
                <div className="flex gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleCancelEdit} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleSaveProfile} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-indigo-800 transition-colors shadow-sm">
                                Save Changes
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setIsEditing(true)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                            Edit Profile
                        </button>
                    )}
                </div>
            </div>

            {/* Profile Info */}
            <div className="space-y-6">
                {isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl animate-in fade-in duration-200">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Full Name</label>
                            <input name="name" value={editForm.name} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Role</label>
                            <input name="role" value={editForm.role} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700" />
                        </div>
                         <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Email</label>
                            <input name="email" value={editForm.email} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700" />
                        </div>
                         <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Location</label>
                            <input name="location" value={editForm.location} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700" />
                        </div>
                    </div>
                ) : (
                    <div className="animate-in fade-in duration-200">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{profile.name}</h1>
                        <p className="text-gray-600 dark:text-gray-400 font-medium">{profile.role}</p>
                    </div>
                )}
            </div>

            {!isEditing && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 animate-in fade-in duration-200">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 text-gray-800 dark:text-gray-200">
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full"><Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" /></div>
                            <span className="text-sm font-medium">{profile.email}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-800 dark:text-gray-200">
                             <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full"><Shield className="w-4 h-4 text-gray-600 dark:text-gray-400" /></div>
                            <span className="text-sm font-medium">{profile.role} Access</span>
                        </div>
                         <div className="flex items-center gap-3 text-gray-800 dark:text-gray-200">
                             <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full"><MapPin className="w-4 h-4 text-gray-600 dark:text-gray-400" /></div>
                            <span className="text-sm font-medium">{profile.location}</span>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-sm uppercase tracking-wide">Activity Overview</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-2">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">Certificates Issued</span>
                                <span className="font-bold text-gray-900 dark:text-white">1,245</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">Last Login</span>
                                <span className="font-bold text-gray-900 dark:text-white">Today, 09:42 AM</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* Security Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 transition-colors">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-500 dark:text-gray-400" /> Security Settings
          </h3>
          
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
              <div className="flex items-center justify-between py-4">
                  <div>
                      <div className="font-bold text-gray-900 dark:text-white">Password</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Last changed 3 months ago</div>
                  </div>
                  <button onClick={() => setIsPasswordModalOpen(true)} className="text-primary hover:text-indigo-800 text-sm font-bold border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-indigo-400">
                      Change Password
                  </button>
              </div>

              <div className="flex items-center justify-between py-4">
                  <div>
                      <div className="font-bold text-gray-900 dark:text-white">Two-Factor Authentication</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Add an extra layer of security to your account</div>
                  </div>
                  <button 
                    onClick={toggleTwoFactor}
                    className={`text-sm font-bold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${twoFactorEnabled ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-primary text-white hover:bg-indigo-800'}`}
                  >
                      {twoFactorEnabled ? <><Check className="w-4 h-4" /> Enabled</> : 'Enable'}
                  </button>
              </div>
          </div>
      </div>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Change Password</h3>
                      <button onClick={() => setIsPasswordModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                          <X className="w-5 h-5" />
                      </button>
                  </div>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                      <div>
                          <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Current Password</label>
                          <input type="password" required className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white" placeholder="••••••••" />
                      </div>
                      <div>
                          <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                          <input type="password" required className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white" placeholder="••••••••" />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1 font-medium"><AlertCircle className="w-3 h-3" /> Minimum 8 characters</p>
                      </div>
                      <div>
                          <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
                          <input type="password" required className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white" placeholder="••••••••" />
                      </div>
                      <div className="mt-6 flex justify-end gap-3 pt-4">
                          <button type="button" onClick={() => setIsPasswordModalOpen(false)} className="px-4 py-2 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
                              Cancel
                          </button>
                          <button type="submit" className="px-4 py-2 text-sm font-bold bg-primary text-white rounded-lg hover:bg-indigo-800 shadow-sm">
                              Update Password
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
};
